import React, { useEffect, useRef, useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import BaseFormComponent, { type RequiredIndicator, useFormFieldIds } from '../BaseFormComponent';
import { Button } from '../Button';
import CheckCircleFilled from '../CustomIcons/CheckCircleFilled';
import CopyIcon from '../CustomIcons/CopyIcon';
import EyeClosedIcon from '../CustomIcons/EyeClosedIcon';
import EyeIcon from '../CustomIcons/EyeIcon';
import InfoIcon from '../CustomIcons/InfoIcon';
import FieldMessage from '../FieldMessage';
import LoadingInput from '../LoadingInput';
import { paintedFieldValue } from '../devWarning';
import type { ButtonDefinition, InputSize, InputType, InputVariant } from './types';

interface InputProps {
    label?: string;
    hint?: string;
    tooltip?: string;
    placeholder?: string;
    icon?: React.ReactNode;
    readOnly?: boolean;
    name: FieldPath<FieldValues> | string;
    type?: InputType;
    description?: string;
    size?: InputSize;
    variant?: InputVariant;
    required?: boolean;
    requiredIndicator?: RequiredIndicator;
    error?: boolean;
    loading?: boolean;
    defaultValue?: string;
    disabled?: boolean;
    prefixValue?: string;
    prefixSecondaryValue?: string;
    ellipsis?: boolean;
    formControl?: Control<FieldValues, unknown>;
    buttons?: ButtonDefinition[];
    onCopyClick?: () => void;
    showCopyButton?: boolean;
    onChange?: (value: string) => void;
    /** Controlled value, for a source of truth outside the form (URL, app state). */
    value?: string;
    onBlur?: () => void;
    helperText?: string;
    showPasswordToggle?: boolean;
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
}

// Figma specs one Input, at 42px / px-16 / 15px / radius 14 — the large tier. The smaller
// tiers have no Input master, so they follow Search, which shares the field chrome.
const SIZE_HEIGHT: Record<InputSize, string> = {
    small: 'h-[var(--malachite-control-height-small,28px)]',
    medium: 'h-[var(--malachite-control-height-medium,32px)]',
    large: 'h-[var(--malachite-control-height-large,42px)]',
};

const SIZE_TEXT: Record<InputSize, string> = {
    small: 'text-[length:var(--malachite-font-size-small,13px)]',
    medium: 'text-[length:var(--malachite-font-size-small,13px)]',
    large: 'text-[length:var(--malachite-font-size-15,15px)] leading-[1.4]',
};

const SIZE_PADDING: Record<InputSize, string> = {
    small: 'px-[10px]',
    medium: 'px-[10px]',
    large: 'px-[16px]',
};

const SIZE_RADIUS: Record<InputSize, string> = {
    small: 'rounded-[var(--malachite-radius-10,10px)]',
    medium: 'rounded-xl',
    large: 'rounded-[var(--malachite-radius-xxl,14px)]',
};

const getVariantClasses = (
    variant: InputVariant,
    error?: boolean,
): {
    wrapper: string[];
    shadow: string;
    borderRadius: string;
} => {
    switch (variant) {
        case 'ghost':
            return {
                wrapper: [
                    error
                        ? 'border-0 border-b border-solid  border-[var(--malachite-destructive-border)]'
                        : '',
                    error
                        ? 'focus-within:border-[var(--malachite-destructive-border-hover)] hover:border-[var(--malachite-destructive-border-hover)]'
                        : '',
                ],
                shadow: 'shadow-none',
                borderRadius: 'rounded-none',
            };
        case 'default':
        default:
            return {
                wrapper: [
                    'border border-solid',
                    error
                        ? 'border-[var(--malachite-destructive-border)]'
                        : 'border-[var(--malachite-card-border)]',
                    error
                        ? 'focus-within:border-[var(--malachite-destructive-border-hover)] hover:border-[var(--malachite-destructive-border-hover)]'
                        : 'focus-within:border-[var(--malachite-control-border-hover)] hover:border-[var(--malachite-control-border-hover)]',
                ],
                shadow: error ? 'shadow-error' : 'shadow-default',
                borderRadius: '',
            };
    }
};

const Input: React.FC<InputProps> = ({
    label,
    hint,
    tooltip,
    placeholder,
    icon,
    name,
    required,
    requiredIndicator,
    error,
    description,
    loading = false,
    readOnly = false,
    type = 'text',
    size = 'large',
    variant = 'default',
    defaultValue,
    disabled,
    prefixValue,
    prefixSecondaryValue,
    ellipsis = false,
    formControl,
    buttons,
    onCopyClick,
    showCopyButton,
    onChange,
    value,
    onBlur,
    helperText,
    showPasswordToggle = true,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
}) => {
    const { controlId, labelId, descriptionId, hintId, helperTextId } = useFormFieldIds({
        id,
        label,
        description,
        hint,
        helperText: true,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [inputValue, setInputValue] = useState(defaultValue || '');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => {
                setCopied(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [copied]);

    const handleChange =
        (reportToForm?: (value: unknown) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            onChange?.(newValue);
            reportToForm?.(newValue);
        };

    const formValueRef = useRef<unknown>(undefined);
    const paintedValue = (formValue: unknown): string =>
        paintedFieldValue({
            component: 'Input',
            name,
            value,
            formValue,
            defaultValue,
            bound: !!formControl,
            uncontrolledValue: inputValue,
        });

    const handleCopyClick = onCopyClick
        ? () => {
              const copyValue = paintedValue(formValueRef.current);
              if (copyValue) {
                  navigator.clipboard.writeText(copyValue);
                  setCopied(true);
                  onCopyClick();
              }
          }
        : undefined;

    const actualInputType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

    const hasPrefix = icon || prefixValue || prefixSecondaryValue;

    // The skeleton stands in for the field, so it reads the same height the wrapper does.
    const heightClass = SIZE_HEIGHT[size];

    const buildWrapperClasses = (isError: boolean): string => {
        const variantStyles = getVariantClasses(variant, isError);

        return [
            'relative',
            'flex items-center',
            'w-full',
            variantStyles.borderRadius || SIZE_RADIUS[size],
            disabled
                ? 'bg-[var(--malachite-card-dimmed-background)]'
                : 'bg-[var(--malachite-card-background)]',
            'transition-colors duration-200 ease-in-out',
            variantStyles.shadow,
            ...(disabled ? [] : variantStyles.wrapper),
            SIZE_HEIGHT[size],
        ]
            .filter(Boolean)
            .join(' ');
    };

    return (
        <BaseFormComponent
            label={label}
            hint={hint}
            tooltip={tooltip}
            name={name}
            required={required}
            requiredIndicator={requiredIndicator}
            loading={loading}
            formControl={formControl}
            description={description}
            controlId={controlId}
            labelId={labelId}
            descriptionId={descriptionId}
            hintId={hintId}
            loadingComponent={<LoadingInput heightClass={heightClass} helperText={!!helperText} />}
        >
            {({ error: fieldError, message, onFieldChange, onFieldBlur, value: formValue }) => {
                formValueRef.current = formValue;
                const displayValue = paintedValue(formValue);
                const shouldShowCopyButton =
                    showCopyButton ?? !!(handleCopyClick && disabled && displayValue);
                const isError = error ?? fieldError;
                // `error={false}` opts out of the resolver entirely, message included: a
                // screen that has dealt with the rejection its own way would otherwise keep
                // reporting it in destructive text and through `aria-describedby`.
                // The message is its own line rather than a replacement for the helper text:
                // a field that explains where to find its value still has to say why what was
                // typed was rejected, and dropping one for the other left the field marked red
                // with nothing anywhere saying what was wrong.
                const errorMessage = error === false ? undefined : message;
                // The icon is the only signal a consumer-forced `error` has; beside a message it
                // is duplication, and it crowds the suffix next to a reveal or copy control.
                const showErrorIcon = isError && !errorMessage;
                const hasSuffix =
                    showErrorIcon ||
                    shouldShowCopyButton ||
                    buttons?.length ||
                    (type === 'password' && showPasswordToggle);
                const errorMessageId = `${helperTextId}-error`;
                const describedBy =
                    [
                        descriptionId,
                        hintId,
                        helperText ? helperTextId : undefined,
                        errorMessage ? errorMessageId : undefined,
                        ariaDescribedBy,
                    ]
                        .filter(Boolean)
                        .join(' ') || undefined;

                return (
                    <>
                        <div className={buildWrapperClasses(isError)}>
                            {hasPrefix && (
                                <div className="flex items-center pl-3 pr-1 flex-shrink-0 pointer-events-none">
                                    <CompositeIcon
                                        prefixValue={prefixValue}
                                        prefixSecondaryValue={prefixSecondaryValue}
                                        icon={icon}
                                    />
                                </div>
                            )}

                            <input
                                id={controlId}
                                type={actualInputType}
                                placeholder={placeholder}
                                value={displayValue}
                                onChange={handleChange(onFieldChange)}
                                onBlur={() => {
                                    onBlur?.();
                                    onFieldBlur?.();
                                }}
                                disabled={disabled || readOnly}
                                readOnly={readOnly}
                                autoComplete="off"
                                aria-label={ariaLabel}
                                aria-labelledby={ariaLabelledBy}
                                aria-invalid={isError || undefined}
                                aria-required={required || undefined}
                                aria-describedby={describedBy}
                                className={`flex-1 bg-transparent border-none outline-none text-[var(--malachite-text-primary)] placeholder:text-[var(--malachite-text-placeholder)] disabled:text-[var(--malachite-text-primary)] disabled:cursor-not-allowed ${SIZE_TEXT[size]} ${SIZE_PADDING[size]} ${ellipsis ? 'min-w-0 truncate' : ''}`}
                                name={name}
                            />

                            {hasSuffix && (
                                <div
                                    className={`flex items-center pl-1 flex-shrink-0 ${type === 'password' && showPasswordToggle ? 'pr-0' : 'pr-3'}`}
                                >
                                    <Suffix
                                        error={showErrorIcon}
                                        defaultValue={displayValue}
                                        onCopyClick={handleCopyClick}
                                        showCopyButton={shouldShowCopyButton}
                                        copied={copied}
                                        type={type}
                                        showPassword={showPassword}
                                        showPasswordToggle={showPasswordToggle}
                                        onTogglePassword={() => setShowPassword(!showPassword)}
                                    />
                                    <InputButtons buttons={buttons} />
                                </div>
                            )}
                        </div>
                        {helperText && (
                            <div
                                id={helperTextId}
                                className="text-[length:var(--malachite-font-size-small,13px)] text-left pl-2 text-[var(--malachite-text-dimmed,rgb(161,161,161))]"
                            >
                                {helperText}
                            </div>
                        )}
                        <FieldMessage id={errorMessageId} message={errorMessage} />
                    </>
                );
            }}
        </BaseFormComponent>
    );
};

const CompositeIcon: React.FC<{
    prefixValue?: string;
    prefixSecondaryValue?: string;
    icon?: React.ReactNode;
}> = ({ prefixValue, prefixSecondaryValue, icon }) => {
    return (
        <div className="flex items-center gap-1">
            {icon && (
                <span className="flex items-center justify-center text-[var(--malachite-text-dimmed)] w-4 h-4 flex-shrink-0">
                    {icon}
                </span>
            )}
            {prefixValue && (
                <span className="text-[length:var(--malachite-font-size-medium,14px)] leading-[1.4] text-[var(--malachite-text-dimmed)] whitespace-nowrap">
                    {prefixValue}
                </span>
            )}
            {prefixSecondaryValue && (
                <span className="text-[length:var(--malachite-font-size-medium,14px)] leading-[1.4] text-[var(--malachite-text-dimmed)] whitespace-nowrap">
                    {prefixSecondaryValue}
                </span>
            )}
        </div>
    );
};

const Suffix: React.FC<{
    error?: boolean;
    defaultValue?: string;
    onCopyClick?: () => void;
    showCopyButton?: boolean;
    copied?: boolean;
    type?: InputType;
    showPassword?: boolean;
    showPasswordToggle?: boolean;
    onTogglePassword?: () => void;
}> = ({
    error,
    defaultValue,
    onCopyClick,
    showCopyButton = false,
    copied = false,
    type,
    showPassword,
    showPasswordToggle = true,
    onTogglePassword,
}) => {
    return (
        <div className="flex items-center gap-1">
            {error && (
                // Decorative: the rejection is carried by the message beneath the field, so
                // announcing an unnamed icon here would only repeat it as "info".
                <div
                    aria-hidden="true"
                    className="flex items-center justify-center w-5 h-5 flex-shrink-0"
                >
                    <InfoIcon
                        size={16}
                        className="text-[var(--malachite-destructive-foreground,rgb(196,62,62))]"
                    />
                </div>
            )}
            {showCopyButton && onCopyClick && defaultValue && (
                <button
                    onClick={onCopyClick}
                    type="button"
                    className="flex items-center justify-center w-5 h-5 hover:bg-[var(--malachite-primary-border)] rounded transition-colors flex-shrink-0 cursor-pointer"
                >
                    {copied ? (
                        <CheckCircleFilled
                            size={16}
                            style={{ color: 'var(--malachite-success-foreground)' }}
                        />
                    ) : (
                        <CopyIcon size={16} />
                    )}
                </button>
            )}
            {type === 'password' && showPasswordToggle && (
                <Button variant="no-style" onClick={onTogglePassword} type="button" size="small">
                    {showPassword ? (
                        <EyeClosedIcon
                            size={16}
                            style={{ color: 'var(--malachite-text-dimmed)' }}
                        />
                    ) : (
                        <EyeIcon
                            size={16}
                            style={{ color: 'var(--malachite-text-dimmed)' }}
                        />
                    )}
                </Button>
            )}
        </div>
    );
};

const InputButtons: React.FC<{
    buttons?: ButtonDefinition[];
}> = ({ buttons }) => {
    if (!buttons?.length) {
        return null;
    }

    return (
        <>
            {buttons.map((button) => (
                <Button
                    key={button.label}
                    variant={button.variant || (button.danger ? 'destructive' : 'outline')}
                    size={button.size || 'small'}
                    disabled={button.disabled}
                    icon={button.icon}
                    tooltip={button.tooltip}
                    onClick={button.onClick}
                    className="ml-1"
                >
                    {button.label}
                </Button>
            ))}
        </>
    );
};

export default Input;
